import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { LegalDialog } from '../legal-dialog/legal-dialog';
import { CONSENT_LIFETIME_MONTHS } from '../../../core/data/consent.config';
import { ConsentService } from '../../../core/services/consent.service';
import { LanguageService } from '../../../core/services/language.service';
import { isPlainClick } from '../../../core/utils/plain-click';

/**
 * The audience-measurement banner: Accept and Refuse, equal in size, weight and placement,
 * told apart by colour only.
 *
 * Drawn in the browser only and fixed over the page, so the prerender never contains it
 * and it cannot shift the layout. It takes focus only when "Cookie settings" reopens it;
 * on a first visit it waits right after the skip link in the tab order.
 */
@Component({
  selector: 'av-consent-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, LegalDialog],
  template: `
    @if (rendered()) {
      @if (consent.bannerOpen()) {
        <section
          #panel
          class="cb"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cb-title"
          aria-describedby="cb-body"
          tabindex="-1"
          (keydown.escape)="dismiss()"
        >
          <div class="cb__inner av-container av-container--wide">
            <div class="cb__text">
              <p id="cb-title" class="cb__title">{{ 'consent.title' | translate }}</p>
              <div id="cb-body" class="cb__body">
                <p>{{ 'consent.body' | translate }}</p>
                <p>
                  {{ 'consent.note' | translate: { months: lifetimeMonths } }}
                  <a class="cb__link" [href]="privacyHref()" (click)="openPrivacy($event)">{{
                    'consent.privacyLink' | translate
                  }}</a>
                </p>
              </div>
              @if (current(); as answer) {
                <p class="cb__current">
                  {{ 'consent.current.' + answer.choice | translate: { date: answer.date } }}
                </p>
              }
            </div>
            <div class="cb__actions">
              <button type="button" class="btn cb__btn cb__btn--refuse" (click)="refuse()">
                {{ 'consent.refuse' | translate }}
              </button>
              <button type="button" class="btn cb__btn cb__btn--accept" (click)="accept()">
                {{ 'consent.accept' | translate }}
              </button>
            </div>
          </div>
        </section>
      }

      <av-legal-dialog
        [href]="privacyHref()"
        [open]="privacyOpen()"
        [label]="'footer.privacy' | translate"
        (closed)="privacyOpen.set(false)"
      />
    }
  `,
  styleUrl: './consent-banner.scss',
})
export class ConsentBanner {
  protected readonly consent = inject(ConsentService);
  private readonly language = inject(LanguageService);
  private readonly injector = inject(Injector);

  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  protected readonly rendered = signal(false);
  protected readonly privacyOpen = signal(false);
  protected readonly lifetimeMonths = CONSENT_LIFETIME_MONTHS;

  protected readonly privacyHref = computed(() => `/privacy-${this.language.lang()}.html`);

  protected readonly current = computed(() => {
    const stored = this.consent.stored();
    if (!stored) return null;

    const date = new Intl.DateTimeFormat(this.language.lang(), { dateStyle: 'long' }).format(
      new Date(stored.at),
    );
    return { choice: stored.choice, date };
  });

  /** Where focus goes back to once a reopened banner closes: the "Cookie settings" link. */
  private returnFocusTo: HTMLElement | null = null;

  constructor() {
    afterNextRender(() => this.rendered.set(this.consent.enabled));

    effect(() => {
      const requested = this.rendered() && this.consent.reopenRequests() > 0;
      if (requested) untracked(() => this.focusReopenedPanel());
    });

    effect((onCleanup) => {
      const panel = this.panel()?.nativeElement;
      if (panel) onCleanup(this.reserveScrollRoomFor(panel));
    });
  }

  protected accept(): void {
    this.closeWith(() => this.consent.accept());
  }

  protected refuse(): void {
    this.closeWith(() => this.consent.refuse());
  }

  protected dismiss(): void {
    if (this.consent.stored()) this.closeWith(() => this.consent.dismiss());
  }

  protected openPrivacy(event: MouseEvent): void {
    if (!isPlainClick(event)) return;

    event.preventDefault();
    this.privacyOpen.set(true);
  }

  /** Keeps a focused element from scrolling in behind the banner; the page itself never moves. */
  private reserveScrollRoomFor(panel: HTMLElement): () => void {
    const root = document.documentElement;
    const reserve = () =>
      root.style.setProperty('--consent-inset', `calc(${panel.offsetHeight}px + 2.5rem)`);
    const observer = new ResizeObserver(reserve);
    reserve();
    observer.observe(panel);

    return () => {
      observer.disconnect();
      root.style.removeProperty('--consent-inset');
    };
  }

  private focusReopenedPanel(): void {
    const active = document.activeElement;
    this.returnFocusTo = active instanceof HTMLElement ? active : null;

    afterNextRender(() => this.panel()?.nativeElement.focus(), { injector: this.injector });
  }

  /** Moves focus out before the banner leaves the DOM, so it never drops to the body. */
  private closeWith(answer: () => void): void {
    const target = this.returnFocusTo?.isConnected
      ? this.returnFocusTo
      : document.getElementById('main');
    this.returnFocusTo = null;

    const focusWasInside = this.panel()?.nativeElement.contains(document.activeElement);
    answer();
    if (focusWasInside) target?.focus({ preventScroll: true });
  }
}
